// Normative timings (design D3). All animation collapses under
// `prefers-reduced-motion: reduce`; the latency thresholds do not — they are
// behavior, not motion.

/** Typing bubble gains the reassurance caption past this point. */
export const SLOW_TURN_MS = 4000;

/** A turn that has not resolved by here becomes a retryable error bubble.
 *  35s clears the backend's own 30-second retrieval ceiling with margin: at
 *  15s this timer raced turns the pipeline legitimately finishes — and
 *  commits — in 20–30s, so the bubble was discarding real replies (store
 *  tracker 6.29). */
export const TURN_TIMEOUT_MS = 35000;

/** Minimum "thinking" beat so an instant reply doesn't flash. */
export const MIN_THINKING_MS = 1000;

/** Pinned-card FLIP float from the start card's captured rect. */
export const FLIP_MS = 820;
export const FLIP_EASING = 'cubic-bezier(.22,1,.3,1)';

export const SLOW_TURN_CAPTION = "Still with you — this one's taking a moment.";

/**
 * A failed call or a server-declared retryable `extraction_error`. This copy
 * may promise nothing was lost because that claim rides the platform
 * guarantee: `retryable: true` means nothing was written.
 */
export const TURN_ERROR_MESSAGE =
    'Sorry — I hit a snag reading that. Nothing was lost; tap Try again and I’ll take another look.';

/**
 * The client-timeout surface (store tracker 6.29). At timeout the client
 * cannot know whether the backend will still commit the turn — the 15s timer
 * raced a 23.6s turn that then persisted its fills — so this copy makes no
 * nothing-was-lost claim.
 */
export const TURN_TIMEOUT_MESSAGE =
    'Sorry — this is taking longer than expected. Tap Try again and I’ll take another look.';

/**
 * Shown alongside a reply whose extraction failed unretryably. The answer stands
 * on its own, but a student who volunteered details would otherwise assume they
 * were recorded — the receipts principle says a fill is never invisible, and
 * that has to hold for a fill that did not happen. Deliberately offers
 * rephrasing rather than a retry: the same message through the same defect
 * fails the same way.
 */
export const UNRECORDED_FILL_NOTICE =
    'I couldn’t record details from that message — you can try rephrasing.';

/**
 * The partial variant (platform tracker 6.17): the same unretryable failure,
 * but the turn also carried fills, so receipts render right beside this notice.
 * Claiming *nothing* was recorded next to a list of recorded fields would be a
 * lie in the other direction — only some details were lost.
 */
export const PARTIAL_FILL_NOTICE =
    'I couldn’t record some details from that message — you can try rephrasing.';

/**
 * The quiet fills-retry affordance (engagement closure, store tracker 6.55):
 * the reply above stood on its own — this never masks it — but its own fill
 * attempt failed in a way the server guarantees wrote nothing, so retrying is
 * honest. Deliberately distinct from `TURN_ERROR_MESSAGE`'s full-failure
 * card, which is reserved for a turn with no answer at all.
 */
export const RETRYABLE_FILL_NOTICE =
    'I might not have caught the details in that one — want me to try again?';
export const DISCARD_CONFIRM_QUESTION = 'Discard this form? Your answers will be cleared.';
export const DISCARD_ACKNOWLEDGEMENT =
    'No problem — I’ve cleared that form and its answers. If it was the wrong one, just tell me which you’d like and we’ll start fresh.';
export const SUBMITTED_ACKNOWLEDGEMENT =
    'Submitted! The admissions office has your application — I’ll be right here if you have questions in the meantime.';

/* ---------------- Multiform acknowledgements (design D5) ----------------
   Every transition narrates in chat so the log stays a truthful history of
   what happened to which form. Copy follows the design prototype. */

/** Switching away from an active form: name both, promise nothing is lost.
 *  No possessive prefix — the default config-form name is already "Your
 *  Application". */
export const switchAcknowledgement = (next: string, prev: string): string =>
    `Switched to the ${next} form. ${prev} is saved in your forms — switch back anytime.`;

/** Discard with a queued replacement: the clearing plus a fresh offer. */
export const discardWithQueueAcknowledgement = (next: string): string =>
    `No problem — I’ve cleared that form and its answers. Want to start the ${next} form we queued instead?`;

/** Post-submit handoff to the queue head. */
export const submittedNextUpAcknowledgement = (next: string): string =>
    `Submitted! Next up — the ${next} form we queued earlier. Ready when you are.`;
export const SUBMIT_FAILURE_MESSAGE =
    'Couldn’t reach the server just now. Your answers are saved on this device — nothing was lost.';

/* ---------------- Post-submit corrections (design D1–D5) ----------------
   Review mode is a variant of the existing overlay: the end-card line and
   submit label flip on `dirty`, and the failure/retry path is the same
   `SUBMIT_FAILURE_MESSAGE` above — corrections reuse submit semantics
   wholesale (D3), only the copy below is new. */

/** End-card line, review mode, clean (no unsent edits). */
export const REVIEW_CLEAN_LINE = 'Submitted. Tap any answer to correct it.';

/** End-card line, review mode, dirty — pairs with the Update submission button. */
export const REVIEW_DIRTY_LINE = 'You’ve corrected answers — send the update?';

/** Submit button label once a submitted form has unsent edits (D1). */
export const UPDATE_SUBMISSION_LABEL = 'Update submission';

/** Pinned-area warning row label for a dirty form (D2: "a persistent, visible home"). */
export const EDITS_NOT_SENT_LABEL = 'Edits not sent yet';

/** Forms-menu status for a submitted form with unsent edits. */
export const MENU_STATUS_EDITS_NOT_SENT = 'Submitted · edits not sent';

/**
 * Chat acknowledgement on a successful update (D3/D5): light, no success
 * overlay — a correction re-post is not a fresh submission. The widget emits
 * no analytics for this; `FormUpdated` is the backend's single-emitter event
 * at the write (platform amendment 2026-07-31).
 */
export const updateAcknowledgement = (formShort: string): string =>
    `I’ve updated your ${formShort}. The office will see the latest version.`;
